import { useEffect, useState } from "react";
import {
  SIZE_PRESETS,
  CONTROL_OPTIONS,
  FRAME_COLORS,
  FABRIC_TYPES,
  FABRIC_COLORS,
  ACCESSORIES,
  INSTALLATION_OPTIONS,
  DEFAULT_CONFIG,
  SIZE_DEPENDENT_PRICES,
  DATASHEET_URL,
  formatHUF,
} from "../lib/constants";
import { HU_LOCATIONS } from "../lib/hu-locations";
import { fetchQuote } from "../lib/api";
import { Check, Mail, Sparkles, FileText, Lightbulb, Cloud, Smartphone, Cog } from "lucide-react";
import Pergola3D from "./Pergola3D";
import QuoteRequestDialog from "./QuoteRequestDialog";

const ICONS = { lightbulb: Lightbulb, cloud: Cloud, smartphone: Smartphone, cog: Cog };

export default function Configurator() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [price, setPrice] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [locQuery, setLocQuery] = useState("");

  useEffect(() => {
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const data = await fetchQuote(config);
        if (!cancel) setPrice(data.total_huf);
      } catch {
        // ignore
      }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [config]);

  const update = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const toggleAcc = (id) =>
    setConfig((c) => ({
      ...c,
      accessories: c.accessories.includes(id)
        ? c.accessories.filter((a) => a !== id)
        : [...c.accessories, id],
    }));

  const sizeExtras = SIZE_DEPENDENT_PRICES[config.size_id] || {};
  const filteredLocations = locQuery.length >= 2
    ? HU_LOCATIONS.filter((l) => l.toLowerCase().includes(locQuery.toLowerCase())).slice(0, 20)
    : [];

  const handleOpenQuote = () => setQuoteOpen(true);

  return (
    <section
      id="configurator"
      data-testid="configurator-section"
      className="py-24 sm:py-32 bg-[#031a20] text-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="mb-16 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
            <span className="gold-rule" />Egyedi konfiguráció
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
            Tervezze meg saját kézzel összeállított pergoláját.
          </h2>
          <p className="mt-6 text-base text-white/70 leading-relaxed">
            A 3D modellen valós időben látszik minden választása – színek, motorvezérlés, LED, szenzorok.
          </p>
          <a
            href={DATASHEET_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="datasheet-link"
            className="mt-6 inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-[#e5c865] transition-colors border border-[#d4af37]/40 hover:border-[#d4af37] px-4 py-2"
          >
            <FileText className="w-4 h-4" />
            Termékadatlap (PDF)
          </a>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* 3D + Price (sticky) */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Pergola3D config={config} />
              <div className="bg-[#04222a] border border-[#d4af37]/20 p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#d4af37]">
                  Becsült végösszeg (bruttó)
                </div>
                <div data-testid="configurator-price" className="font-serif text-4xl text-white mt-2">
                  {price !== null ? formatHUF(price) : "—"}
                </div>
                <button
                  data-testid="submit-quote-button"
                  onClick={handleOpenQuote}
                  className="mt-5 w-full bg-[#d4af37] text-[#031a20] hover:bg-[#e5c865] py-4 uppercase tracking-[0.2em] text-xs transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Mail className="w-4 h-4" /> Ajánlatkérés beküldése
                </button>
                <p className="mt-3 text-[11px] text-white/50">
                  Az ár ÁFA-val (27%) és kiszállítással értendő. A 3D modellt egérrel forgathatja.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 space-y-10">
            {/* Size */}
            <Step num="01" title="Méret választása">
              <div className="grid sm:grid-cols-3 gap-3">
                {SIZE_PRESETS.map((s) => (
                  <SelectableCard
                    key={s.id}
                    testid={`size-${s.id}`}
                    active={config.size_id === s.id}
                    onClick={() => update({ size_id: s.id })}
                    badge={s.popular ? "Népszerű" : null}
                  >
                    <div className="font-serif text-2xl text-white">{s.label}</div>
                    <div className="text-xs text-white/60 mt-1">{s.desc}</div>
                    <div className="mt-3 text-sm font-medium text-[#d4af37]">{formatHUF(s.price)} <span className="text-white/50 text-[10px]">bruttó</span></div>
                  </SelectableCard>
                ))}
              </div>
            </Step>

            {/* Control */}
            <Step num="02" title="Vezérlés">
              <div className="grid sm:grid-cols-2 gap-3">
                {CONTROL_OPTIONS.map((o) => (
                  <SelectableCard
                    key={o.id}
                    testid={`control-${o.id}`}
                    active={config.control === o.id}
                    onClick={() => update({ control: o.id })}
                  >
                    <div className="font-medium text-white">{o.label}</div>
                    <div className="text-xs text-white/60 mt-1">{o.desc}</div>
                    <div className="mt-3 text-xs text-[#d4af37]">
                      {o.price === 0 ? "Alapár tartalmazza" : `+${formatHUF(o.price)} bruttó`}
                    </div>
                  </SelectableCard>
                ))}
              </div>
            </Step>

            {/* Fabric type */}
            <Step num="03" title="Szövet típusa">
              <div className="grid sm:grid-cols-2 gap-3">
                {FABRIC_TYPES.map((f) => {
                  const extra = f.id === "w96" ? (sizeExtras.w96_premium || 0) : 0;
                  return (
                    <SelectableCard
                      key={f.id}
                      testid={`fabric-type-${f.id}`}
                      active={config.fabric_type === f.id}
                      onClick={() => update({ fabric_type: f.id })}
                    >
                      <div className="font-medium text-white">{f.name}</div>
                      <div className="text-xs text-white/60 mt-1">{f.desc}</div>
                      <div className="mt-3 text-xs text-[#d4af37]">
                        {extra === 0 ? "Alapár tartalmazza" : `+${formatHUF(extra)} bruttó`}
                      </div>
                    </SelectableCard>
                  );
                })}
              </div>
            </Step>

            {/* Fabric color */}
            <Step num="04" title="Szövet színe">
              <div className="grid grid-cols-5 gap-3">
                {FABRIC_COLORS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`fabric-color-${c.id}`}
                    onClick={() => update({ fabric_color: c.id })}
                    className="flex flex-col items-center gap-2 group"
                  >
                    <span
                      className={`w-12 h-12 border-2 transition-all ${
                        config.fabric_color === c.id
                          ? "border-[#d4af37] ring-2 ring-[#d4af37]/40 ring-offset-2 ring-offset-[#031a20]"
                          : "border-white/20 group-hover:border-white/50"
                      }`}
                      style={{ background: c.hex }}
                    />
                    <span className="text-[10px] text-white/70">{c.name}</span>
                  </button>
                ))}
              </div>
            </Step>

            {/* Frame */}
            <Step num="05" title="Szerkezet színe">
              <FrameGroup
                title="Standard (alapár)"
                colors={FRAME_COLORS.filter((c) => c.group === "standard")}
                config={config}
                update={update}
                extra={0}
              />
              <FrameGroup
                title={`Egyedi RAL (+${formatHUF(sizeExtras.custom_ral || 0)} bruttó)`}
                colors={FRAME_COLORS.filter((c) => c.group === "custom")}
                config={config}
                update={update}
                extra={sizeExtras.custom_ral || 0}
              />
              <FrameGroup
                title={`Fa hatású (+${formatHUF(sizeExtras.wood || 0)} bruttó)`}
                colors={FRAME_COLORS.filter((c) => c.group === "special")}
                config={config}
                update={update}
                extra={sizeExtras.wood || 0}
              />

              {config.frame_color === "custom" && (
                <div className="mt-4 p-4 border border-[#d4af37]/30 bg-[#d4af37]/5">
                  <label className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2 block">
                    RAL kód megadása
                  </label>
                  <input
                    data-testid="custom-ral-code-input"
                    type="text"
                    value={config.custom_ral_code || ""}
                    onChange={(e) => update({ custom_ral_code: e.target.value })}
                    placeholder="pl. RAL 7016"
                    maxLength={20}
                    className="w-full bg-[#031a20] border border-white/20 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37] px-4 py-2 text-sm text-white placeholder-white/30"
                  />
                  <div className="text-[11px] text-white/60 mt-2">
                    Adja meg a kívánt RAL színkódot — a gyártás során ezt használjuk.
                  </div>
                </div>
              )}
            </Step>

            {/* Accessories */}
            <Step num="06" title="Kiegészítők & opciók">
              <div className="space-y-6">
                {ACCESSORIES.map((cat) => {
                  const Icon = ICONS[cat.icon] || Sparkles;
                  return (
                    <div key={cat.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
                        <h4 className="text-sm uppercase tracking-[0.2em] text-white/90">{cat.title}</h4>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {cat.options.map((o) => {
                          const active = config.accessories.includes(o.id);
                          let dynamicPrice = o.price || 0;
                          if (o.id === "led_spot") dynamicPrice = sizeExtras.led_spot || 0;
                          if (o.id === "led_box") dynamicPrice = sizeExtras.led_box || 0;
                          return (
                            <button
                              key={o.id}
                              data-testid={`accessory-${o.id}`}
                              onClick={() => toggleAcc(o.id)}
                              className={`text-left p-4 border transition-colors flex items-start gap-3 ${
                                active
                                  ? "border-[#d4af37] bg-[#d4af37]/10"
                                  : "border-white/10 hover:border-white/40"
                              }`}
                            >
                              <span className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                active ? "bg-[#d4af37] border-[#d4af37]" : "border-white/40"
                              }`}>
                                {active && <Check className="w-3.5 h-3.5 text-[#031a20]" />}
                              </span>
                              <div>
                                <div className="text-sm font-medium text-white">{o.name}</div>
                                <div className="text-xs text-white/60 mt-1">{o.desc}</div>
                                <div className="text-xs text-[#d4af37] mt-2">
                                  +{formatHUF(dynamicPrice)} <span className="text-white/50">bruttó</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Step>

            {/* Installation */}
            <Step num="07" title="Telepítés">
              <div className="grid sm:grid-cols-2 gap-3">
                {INSTALLATION_OPTIONS.map((o) => (
                  <SelectableCard
                    key={o.id}
                    testid={`installation-${o.id}`}
                    active={config.installation === o.id}
                    onClick={() => update({ installation: o.id })}
                    badge={o.id === "with" ? "Ajánlott" : null}
                  >
                    <div className="font-medium text-white">{o.label}</div>
                    <div className="text-xs text-white/60 mt-1">{o.desc}</div>
                    <div className="mt-3 text-xs text-[#d4af37]">
                      {o.price === 0 ? "Ingyenes" : `+${formatHUF(o.price)} bruttó`}
                    </div>
                  </SelectableCard>
                ))}
              </div>
            </Step>

            {/* Location */}
            <Step num="08" title="Telepítés helyszíne">
              <label className="text-xs uppercase tracking-[0.2em] text-white/70 mb-2 block">
                Település (gépeljen a kereséshez)
              </label>
              <div className="relative">
                <input
                  data-testid="location-input"
                  type="text"
                  value={config.location || locQuery}
                  onChange={(e) => {
                    setLocQuery(e.target.value);
                    update({ location: "" });
                  }}
                  placeholder="pl. Budapest, Debrecen, Szeged..."
                  className="w-full bg-[#04222a] border border-white/20 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37] px-4 py-3 text-sm text-white"
                />
                {filteredLocations.length > 0 && !config.location && (
                  <ul
                    data-testid="location-suggestions"
                    className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-[#031a20] border border-[#d4af37]/40 shadow-lg"
                  >
                    {filteredLocations.map((loc) => (
                      <li key={loc}>
                        <button
                          type="button"
                          onClick={() => {
                            update({ location: loc });
                            setLocQuery(loc);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#d4af37]/15"
                        >
                          {loc}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {config.location && (
                <div className="mt-2 text-xs text-[#d4af37]">Kiválasztva: {config.location}</div>
              )}
            </Step>
          </div>
        </div>
      </div>

      <QuoteRequestDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        config={config}
        price={price}
      />
    </section>
  );
}

function Step({ num, title, children }) {
  return (
    <div className="border-t border-white/10 pt-8">
      <div className="flex items-baseline gap-4 mb-6">
        <span className="font-serif text-3xl text-[#d4af37]">{num}</span>
        <h3 className="font-serif text-2xl text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function SelectableCard({ active, onClick, children, badge, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative text-left p-5 border transition-colors ${
        active
          ? "border-[#d4af37] bg-[#d4af37]/10"
          : "border-white/10 hover:border-white/40 bg-[#04222a]"
      }`}
    >
      {badge && (
        <span className="absolute -top-2 left-4 bg-[#d4af37] text-[#031a20] text-[10px] uppercase tracking-wider px-2 py-0.5">
          {badge}
        </span>
      )}
      {children}
    </button>
  );
}

function FrameGroup({ title, colors, config, update, extra }) {
  if (!colors.length) return null;
  return (
    <div className="mt-4 first:mt-0">
      <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">{title}</div>
      <div className="flex flex-wrap gap-3">
        {colors.map((c) => (
          <button
            key={c.id}
            data-testid={`frame-color-${c.id}`}
            onClick={() => update({ frame_color: c.id })}
            className={`flex items-center gap-3 p-3 border transition-colors ${
              config.frame_color === c.id
                ? "border-[#d4af37] bg-[#d4af37]/10"
                : "border-white/10 hover:border-white/40"
            }`}
          >
            <span
              className="w-8 h-8 border border-white/20"
              style={{ background: c.hex }}
            />
            <span className="text-xs text-white">{c.name}</span>
          </button>
        ))}
      </div>
      {/* extra is reflected in the group title; no per-card price needed */}
    </div>
  );
}
